import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "./ui/card";
import { ArrowRight, ArrowDown, Info } from "lucide-react";

const nodes = [
  {
    id: "csharp",
    label: "C# Code",
    description: "Write application logic using LINQ and entity objects.",
    color: "from-primary/25 to-primary/10",
    bullets: ["Entities & DbContext", "LINQ queries", "Business logic layer"],
  },
  {
    id: "efcore",
    label: "EF Core",
    description:
        "Translates LINQ to SQL, tracks changes, and manages connections.",
    color: "from-secondary/25 to-secondary/10",
    bullets: ["Change tracking", "Migrations", "Provider abstraction"],
  },
  {
    id: "sql",
    label: "SQL",
    description: "Generated SQL optimized for your chosen provider.",
    color: "from-accent/25 to-accent/10",
    bullets: ["Parameterized queries", "Joins & projections", "Query optimization"],
  },
  {
    id: "database",
    label: "Database",
    description: "Persistent storage (SQL Server, PostgreSQL, SQLite, etc.).",
    color: "from-primary/25 to-primary/10",
    bullets: ["Indexes & constraints", "Transactions", "Durable storage"],
  },
];

const cardVariants = {
  rest: { y: 0, scale: 1 },
  hover: { y: -3, scale: 1.02 },
};

const glowVariants = {
  off: { opacity: 0 },
  on: { opacity: 1 },
};

const isActive = (id, hovered, selected) => id === (selected || hovered);

const InteractiveDiagram = () => {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);

  const activeId = selected || hovered || "csharp";

  const activeNode = useMemo(() => {
    return nodes.find((n) => n.id === activeId) || nodes[0];
  }, [activeId]);

  const clearHoverIfNotSelected = () => {
    if (!selected) setHovered(null);
  };

  return (
      <div className="mx-auto max-w-5xl">
        {/* Flow row */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch lg:gap-0">
          {nodes.map((node, index) => {
            const active = isActive(node.id, hovered, selected);
            const pinned = selected === node.id;

            return (
                <React.Fragment key={node.id}>
                  <motion.div
                      className="relative flex-1"
                      initial="rest"
                      whileHover="hover"
                      animate="rest"
                      variants={cardVariants}
                      onMouseEnter={() => setHovered(node.id)}
                      onMouseLeave={clearHoverIfNotSelected}
                      onFocus={() => setHovered(node.id)}
                      onBlur={clearHoverIfNotSelected}
                  >
                    {/* subtle glow layer */}
                    <motion.div
                        className="pointer-events-none absolute -inset-1 rounded-xl bg-primary/15 blur-xl"
                        variants={glowVariants}
                        animate={active ? "on" : "off"}
                        transition={{ duration: 0.2 }}
                    />

                    <Card
                        role="button"
                        tabIndex={0}
                        aria-pressed={pinned}
                        onClick={() =>
                            setSelected((prev) => (prev === node.id ? null : node.id))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelected((prev) => (prev === node.id ? null : node.id));
                          }
                          if (e.key === "Escape") setSelected(null);
                        }}
                        className={[
                          "relative h-full cursor-pointer border-2 transition-all duration-200",
                          active
                              ? "border-primary shadow-md"
                              : "border-border hover:border-primary/50",
                          pinned ? "ring-2 ring-primary/30" : "",
                          "rounded-xl",
                        ].join(" ")}
                    >
                      <CardContent className="p-6">
                        <div
                            className={[
                              "mb-4 rounded-lg bg-gradient-to-br p-4 text-center",
                              node.color,
                            ].join(" ")}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <h3 className="text-lg font-semibold">{node.label}</h3>
                            {pinned ? (
                                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                            Pinned
                          </span>
                            ) : null}
                          </div>
                        </div>

                        <motion.p
                            className="text-sm text-muted-foreground"
                            initial={false}
                            animate={{ opacity: active ? 1 : 0.72 }}
                            transition={{ duration: 0.15 }}
                        >
                          {node.description}
                        </motion.p>

                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Connector */}
                  {index < nodes.length - 1 && (
                      <>
                        {/* Desktop connector */}
                        <div className="hidden w-16 items-center justify-center lg:flex">
                          <motion.div
                              animate={
                                hovered === node.id ||
                                hovered === nodes[index + 1].id ||
                                selected === node.id ||
                                selected === nodes[index + 1].id
                                    ? { x: [0, 8, 0], opacity: [0.7, 1, 0.7] }
                                    : { x: 0, opacity: 0.45 }
                              }
                              transition={{ duration: 1.1, repeat: Infinity }}
                          >
                            <ArrowRight className="h-8 w-8 text-primary" />
                          </motion.div>
                        </div>

                        {/* Mobile connector */}
                        <div className="flex items-center justify-center lg:hidden">
                          <motion.div
                              animate={
                                hovered === node.id ||
                                hovered === nodes[index + 1].id ||
                                selected === node.id ||
                                selected === nodes[index + 1].id
                                    ? { y: [0, 6, 0], opacity: [0.7, 1, 0.7] }
                                    : { y: 0, opacity: 0.45 }
                              }
                              transition={{ duration: 1.1, repeat: Infinity }}
                          >
                            <ArrowDown className="h-7 w-7 text-primary" />
                          </motion.div>
                        </div>
                      </>
                  )}
                </React.Fragment>
            );
          })}
        </div>

        {/* Details panel */}
        <div className="mt-6">
          <AnimatePresence mode="wait">
            <motion.div
                key={activeNode.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
            >
              <Card className="rounded-xl border border-border">
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm text-muted-foreground">
                        Currently viewing
                      </div>
                      <div className="text-xl font-semibold">
                        {activeNode.label}
                      </div>
                    </div>

                    {selected ? (
                        <button
                            type="button"
                            className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted"
                            onClick={() => setSelected(null)}
                        >
                          Clear pin
                        </button>
                    ) : (
                        <div className="text-sm text-muted-foreground">
                          Tip: Click a card to pin it
                        </div>
                    )}
                  </div>

                  <p className="mt-3 text-sm text-muted-foreground">
                    {activeNode.description}
                  </p>

                  <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {activeNode.bullets.map((b) => (
                        <div
                            key={b}
                            className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm"
                        >
                          {b}
                        </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
  );
};

export default InteractiveDiagram;
